import React, { useCallback, useEffect, useRef, useState } from "react";
import "./AboutIntroSlider.css";

// ─────────────────────────────────────────────────────────────────────────────
//  AboutIntroSlider
//  Pure-React, API-driven slider used in the About Us intro section.
//
//  Designed for visual + behavioural parity with the HTML reference
//  .about-slider-wrapper.owl-carousel, which is initialized in
//  assets/js/author.js like this:
//
//      $('.about-slider-wrapper').owlCarousel({
//          loop: true,
//          margin: 0,
//          nav: false,        // no arrows
//          dots: true,
//          items: 1,
//          autoplay: true,    // default autoplayTimeout: 5000ms
//                             // default smartSpeed:        250ms
//                             // default autoplayHoverPause:false
//      });
//
//  Matching details:
//    • Horizontal translateX slide (owl default — no animateIn/Out).
//    • 250ms ease transition (owl smartSpeed default).
//    • 5000ms autoplay interval (owl autoplayTimeout default).
//    • No arrows (nav:false).
//    • No hover-pause (autoplayHoverPause:false default).
//    • No keyboard navigation (owl has none by default).
//    • Container height = max natural aspect ratio of all images
//      (owl autoHeight:false + items:1 ⇒ height is the tallest
//      item's natural height).
//    • Loop (wraps from last → first and vice-versa, same as
//      owl loop:true).
// ─────────────────────────────────────────────────────────────────────────────

// Normalises whatever the admin uploaded. Accepts:
//   - "uploads/about-us/foo.jpg"        (admin-uploaded)
//   - "/uploads/about-us/foo.jpg"       (already prefixed)
//   - "https://cdn.example.com/foo.jpg" (full URL)
// Returns null if the entry is not usable.
const normalizeImageUrl = (entry) => {
  if (!entry) return null;
  const url = typeof entry === "string" ? entry : entry?.url;
  if (!url || typeof url !== "string") return null;
  if (url.startsWith("http") || url.startsWith("/")) return url;
  return `/${url}`;
};

// Maximum height used as a safety cap so a freak portrait upload can't
// blow up the section. The track still flows at the image's natural
// aspect ratio (matches about.html reference design where height is
// driven by the <img>, not the container). Mobile uses a smaller cap
// via a CSS media query.
// Note: previously applied as an inline style; now moved to CSS
// (about-intro-slider.css) so the slide's aspect-ratio wins on
// specificity and the cap only kicks in for very tall images.

const AboutIntroSlider = ({
  images = [],
  autoplay = true,
  interval = 5000, // owl-carousel autoplayTimeout default
  // Optional aspectRatio override (e.g. "16 / 9"). When omitted, the
  // slider renders each image at its natural aspect ratio — same as
  // the static about.html reference (width:100%; height:auto).
  aspectRatio,
}) => {
  // Build a normalised list. The admin can either send:
  //   - string[]  (urls)
  //   - {url, alt?}[] (objects)
  const slides = (Array.isArray(images) ? images : [])
    .map((entry) => {
      const url = normalizeImageUrl(entry);
      if (!url) return null;
      const alt =
        typeof entry === "object" && entry?.alt
          ? entry.alt
          : "About MY Cinemas";
      return { url, alt };
    })
    .filter(Boolean);

  const [activeIndex, setActiveIndex] = useState(0);
  const timerRef = useRef(null);

  const goTo = useCallback(
    (idx) => {
      if (slides.length === 0) return;
      const next = ((idx % slides.length) + slides.length) % slides.length;
      setActiveIndex(next);
    },
    [slides.length],
  );

  // Autoplay — matches owl-carousel autoplayTimeout (default 5000ms)
  // and autoplayHoverPause:false (no pause on hover, no pause on focus).
  useEffect(() => {
    if (!autoplay || slides.length <= 1) return undefined;
    timerRef.current = window.setInterval(() => {
      setActiveIndex((prev) => (prev + 1) % slides.length);
    }, interval);
    return () => {
      if (timerRef.current) window.clearInterval(timerRef.current);
    };
  }, [autoplay, interval, slides.length]);

  if (slides.length === 0) return null;

  const isSingle = slides.length === 1;

  // Compose inline style for the track. The slide's CSS aspect-ratio
  // (about-intro-slider__slide in AboutIntroSlider.css) controls the
  // height in the default case — no inline style needed. When the
  // consumer passes the (optional) `aspectRatio` prop, we lock the
  // box to that ratio instead.
  const trackStyle = aspectRatio ? { aspectRatio } : undefined;

  return (
    <div
      className="about-intro-slider"
      role="region"
      aria-roledescription="carousel"
      aria-label="About MY Cinemas images"
    >
      <div className="about-intro-slider__track" style={trackStyle}>
        <div
          className="about-intro-slider__track-inner"
          style={{
            // Each slide is 100% wide of the track, so translating by
            // (activeIndex * 100%) shows the active slide at position 0.
            // Matches owl-carousel's .owl-stage translate3d behaviour.
            transform: `translateX(-${activeIndex * 100}%)`,
          }}
        >
          {slides.map((slide, i) => (
            <div
              key={`${slide.url}-${i}`}
              className="about-intro-slider__slide"
              role="group"
              aria-roledescription="slide"
              aria-label={`${i + 1} of ${slides.length}`}
              aria-hidden={i !== activeIndex}
            >
              <img src={slide.url} alt={slide.alt} loading="lazy" />
            </div>
          ))}
        </div>
      </div>

      {!isSingle && (
        <div className="about-intro-slider__dots" role="tablist">
          {slides.map((slide, i) => (
            <button
              key={`dot-${i}`}
              type="button"
              className={`about-intro-slider__dot${
                i === activeIndex ? " is-active" : ""
              }`}
              onClick={() => goTo(i)}
              aria-label={`Go to slide ${i + 1}`}
              aria-selected={i === activeIndex}
              role="tab"
            />
          ))}
        </div>
      )}
    </div>
  );
};

export default AboutIntroSlider;